/**
 * AI Trial Room — combined widget:
 * 1. Try-On History (last 10 saved outputs)
 * 2. Batch Try-On (1 customer + up to 5 apparels)
 * 3. Free-form Image Generator (Nano Banana text-to-image)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { History, Layers, ImagePlus, X, Download, Loader2, Trash2, Upload, Sparkles, RefreshCw } from 'lucide-react';
import api from '../../utils/api';

// ─────────────────────────────────────────────────────────
// History Panel
// ─────────────────────────────────────────────────────────
export function TrialRoomHistoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {image_base64, mime_type}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/trial-room/history');
      setItems(r.data?.history || []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openItem = async (id) => {
    try {
      const r = await api.get(`/trial-room/history/${id}`);
      setPreview({ image_base64: r.data.image_base64, mime_type: r.data.mime_type, kind: r.data.kind, meta: r.data.meta });
    } catch (e) { toast.error('Could not load history item'); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this try-on from history?')) return;
    try {
      await api.delete(`/trial-room/history/${id}`);
      toast.success('Deleted');
      load();
    } catch (_) { toast.error('Delete failed'); }
  };

  const downloadPreview = () => {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = `data:${preview.mime_type};base64,${preview.image_base64}`;
    a.download = `TrialRoom-history-${Date.now()}.jpg`;
    a.click();
    toast.success('Saved');
  };

  return (
    <div className="rounded-2xl border-2 border-purple-400/40 bg-gradient-to-br from-purple-950/40 via-slate-950/80 to-fuchsia-950/40 p-4 space-y-3" data-testid="trial-history-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-purple-300 font-black flex items-center gap-1">
            <History className="w-3 h-3" /> Last 10 · Auto-saved
          </div>
          <h3 className="text-lg font-black text-white">📚 Try-On History</h3>
        </div>
        <button onClick={load} className="text-[11px] px-2 py-1 rounded-lg bg-white/10 text-slate-200 flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>
      {loading ? (
        <div className="text-center text-slate-500 py-6 text-xs">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center border-2 border-dashed border-white/10 rounded-xl py-8">
          <History className="w-10 h-10 mx-auto text-slate-500 opacity-60 mb-2" />
          <div className="text-slate-300 text-sm font-bold">Koi try-on saved nahi.</div>
          <div className="text-[10px] text-slate-500 mt-1">Har AI try-on automatically save hoga yahan.</div>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => openItem(it.id)}
              data-testid={`history-item-${it.id}`}
              className="relative rounded-lg overflow-hidden border-2 border-purple-400/30 hover:border-purple-300 aspect-square bg-slate-800 group">
              <div className="absolute inset-0 flex items-center justify-center text-4xl opacity-40 group-hover:opacity-20 transition">
                {it.kind === 'apparel' ? '👗' : it.kind === 'face' ? '💄' : '📦'}
              </div>
              <div className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-black/70 text-purple-300 font-black uppercase">{it.kind}</div>
              <div className="absolute bottom-1 left-1 right-1 text-[9px] text-white bg-black/60 rounded px-1 py-0.5 truncate">
                {it.meta?.product_type || it.meta?.apparel_type || '—'}
              </div>
              <button onClick={(e) => del(it.id, e)} className="absolute top-1 right-1 p-1 rounded bg-red-500/80 opacity-0 group-hover:opacity-100 transition">
                <Trash2 className="w-2.5 h-2.5 text-white" />
              </button>
            </button>
          ))}
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="max-w-2xl w-full bg-slate-950 rounded-2xl border-2 border-purple-400/60 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-black text-white">{preview.kind === 'face' ? '💄 Face' : '👗 Apparel'} Try-On · {preview.meta?.product_type || preview.meta?.apparel_type || ''}</div>
              <button onClick={() => setPreview(null)} className="p-2 rounded bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <img src={`data:${preview.mime_type};base64,${preview.image_base64}`} alt="Preview" className="w-full rounded-xl max-h-[70vh] object-contain" />
            <button onClick={downloadPreview} className="mt-2 w-full px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Download</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Batch Try-On (1 customer + up to 5 apparels)
// ─────────────────────────────────────────────────────────
export function TrialRoomBatchPanel({ onClose }) {
  const [customerImg, setCustomerImg] = useState(null);
  const [apparelImgs, setApparelImgs] = useState([]); // dataURLs
  const [character, setCharacter] = useState('female');
  const [results, setResults] = useState(null); // array of {index, success, image_base64, mime_type}
  const [busy, setBusy] = useState(false);
  const custRef = useRef(null);
  const appRef  = useRef(null);

  const setCust = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = (ev) => setCustomerImg(ev.target.result); r.readAsDataURL(f);
    e.target.value = '';
  };
  const addApparel = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 5 - apparelImgs.length;
    if (remaining <= 0) return toast.error('Max 5 apparels');
    files.slice(0, remaining).forEach((f) => {
      const r = new FileReader();
      r.onload = (ev) => setApparelImgs((prev) => (prev.length < 5 ? [...prev, ev.target.result] : prev));
      r.readAsDataURL(f);
    });
    e.target.value = '';
  };
  const removeApparel = (i) => setApparelImgs((prev) => prev.filter((_, idx) => idx !== i));

  const run = async () => {
    if (!customerImg) return toast.error('Customer photo zaroori');
    if (apparelImgs.length === 0) return toast.error('At least 1 apparel');
    setBusy(true); setResults(null);
    try {
      const r = await api.post('/trial-room/vton-batch', {
        customer_image: customerImg,
        apparel_images: apparelImgs,
        character,
      }, { timeout: 180000 });
      setResults(r.data?.results || []);
      toast.success(`✅ ${r.data?.successful}/${r.data?.total} try-ons ready`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const dl = (res) => {
    const a = document.createElement('a');
    a.href = `data:${res.mime_type};base64,${res.image_base64}`;
    a.download = `Batch-${res.index + 1}-${Date.now()}.jpg`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="trial-batch-panel">
      <div className="p-3 bg-slate-950 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">📦 BATCH TRY-ON</div>
          <h3 className="text-sm font-black text-white">1 Customer × up to 5 Apparels</h3>
        </div>
        <button onClick={onClose} data-testid="batch-close" className="p-2 rounded-lg bg-white/10"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Customer */}
          <div className="rounded-xl border-2 border-purple-400/40 bg-slate-950 p-3">
            <div className="text-[10px] uppercase tracking-widest text-purple-300 font-black mb-2">Step 1 · Customer Photo</div>
            {customerImg ? (
              <div className="flex items-center gap-3">
                <img src={customerImg} alt="cust" className="w-24 h-32 rounded-lg object-cover border border-white/10" />
                <button onClick={() => setCustomerImg(null)} className="text-[11px] text-red-300 hover:text-red-200"><Trash2 className="w-3 h-3 inline" /> Remove</button>
              </div>
            ) : (
              <button onClick={() => custRef.current?.click()} className="px-4 py-3 rounded-lg bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-xs font-black flex items-center gap-1.5"><Upload className="w-4 h-4" /> Upload Customer Photo</button>
            )}
            <input ref={custRef} type="file" accept="image/*" onChange={setCust} className="hidden" />
          </div>
          {/* Apparels */}
          <div className="rounded-xl border-2 border-cyan-400/40 bg-slate-950 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Step 2 · Apparel Photos ({apparelImgs.length}/5)</div>
              <button onClick={() => appRef.current?.click()} disabled={apparelImgs.length >= 5} className="text-[10px] px-2 py-1 rounded bg-cyan-500 text-white font-black disabled:opacity-40">+ Add</button>
              <input ref={appRef} type="file" accept="image/*" multiple onChange={addApparel} className="hidden" />
            </div>
            {apparelImgs.length === 0 ? (
              <div className="text-[11px] text-slate-500 italic text-center py-3">Add 1-5 apparel photos</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {apparelImgs.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                    <img src={img} alt={`app-${i}`} className="w-full h-full object-cover" />
                    <button onClick={() => removeApparel(i)} className="absolute top-1 right-1 p-1 rounded bg-red-500/80"><Trash2 className="w-3 h-3 text-white" /></button>
                    <div className="absolute bottom-1 left-1 text-[9px] px-1 rounded bg-black/70 text-white font-black">#{i + 1}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Character + Run */}
          <div className="rounded-xl border-2 border-amber-400/40 bg-slate-950 p-3">
            <div className="flex items-center gap-2 flex-wrap justify-center mb-3">
              {['male', 'female', 'boy', 'girl'].map((c) => (
                <button key={c} onClick={() => setCharacter(c)} className={`px-3 py-1 rounded-full text-xs font-black ${character === c ? 'bg-fuchsia-500 text-white' : 'bg-white/5 text-slate-300 border border-white/10'}`}>{c}</button>
              ))}
            </div>
            <button onClick={run} disabled={busy || !customerImg || apparelImgs.length === 0} data-testid="batch-run" className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-fuchsia-500 via-purple-500 to-amber-500 text-white text-sm font-black flex items-center justify-center gap-2 disabled:opacity-40 shadow-lg">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing {apparelImgs.length} try-ons in parallel…</> : <><Sparkles className="w-4 h-4" /> 🤖 Run Batch Try-On ({apparelImgs.length})</>}
            </button>
          </div>
          {/* Results */}
          {results && (
            <div className="rounded-xl border-2 border-emerald-400/40 bg-slate-950 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">Results</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {results.map((r) => (
                  <div key={r.index} className="rounded-lg overflow-hidden border border-white/10 bg-slate-900">
                    {r.success ? (
                      <>
                        <img src={`data:${r.mime_type};base64,${r.image_base64}`} alt={`res-${r.index}`} className="w-full aspect-[3/4] object-cover" />
                        <button onClick={() => dl(r)} className="w-full px-2 py-1 bg-emerald-500 text-white text-[10px] font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Save</button>
                      </>
                    ) : (
                      <div className="aspect-[3/4] flex items-center justify-center text-center p-2 text-[10px] text-red-300">
                        <div>❌ #{r.index + 1}<br />{(r.error || 'Failed').slice(0, 60)}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Standalone AI Image Generator (text → image via Nano Banana)
// ─────────────────────────────────────────────────────────
export function TrialRoomImageGen({ onClose }) {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [aspect, setAspect] = useState('square');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    if (prompt.trim().length < 3) return toast.error('Prompt at least 3 chars');
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/trial-room/image-generate', { prompt, style, aspect }, { timeout: 60000 });
      if (r.data?.image_base64) {
        setResult(`data:${r.data.mime_type};base64,${r.data.image_base64}`);
        toast.success('✅ Image generated');
      } else throw new Error('No image');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const dl = () => {
    if (!result) return;
    const a = document.createElement('a'); a.href = result; a.download = `AI-Image-${Date.now()}.jpg`; a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="trial-imgen-panel">
      <div className="p-3 bg-slate-950 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🎨 AI IMAGE GENERATOR</div>
          <h3 className="text-sm font-black text-white">Text → Image · Nano Banana</h3>
        </div>
        <button onClick={onClose} data-testid="imgen-close" className="p-2 rounded-lg bg-white/10"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-2xl mx-auto space-y-3">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the image you want... e.g. 'A cozy Indian sweet shop counter with jalebis and ladoos, warm evening light'"
            maxLength={1500}
            rows={4}
            data-testid="imgen-prompt"
            className="w-full bg-slate-900 border-2 border-amber-400/40 rounded-xl p-3 text-white text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mb-1">Style</div>
              <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="imgen-style" className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-xs text-white">
                <option value="photorealistic">Photorealistic</option>
                <option value="illustration">Illustration</option>
                <option value="anime">Anime</option>
                <option value="oil-painting">Oil Painting</option>
                <option value="3d">3D Render</option>
              </select>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mb-1">Aspect</div>
              <select value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid="imgen-aspect" className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1.5 text-xs text-white">
                <option value="square">Square 1:1</option>
                <option value="portrait">Portrait 3:4</option>
                <option value="landscape">Landscape 16:9</option>
              </select>
            </div>
          </div>
          <button onClick={gen} disabled={busy || prompt.length < 3} data-testid="imgen-run" className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white text-sm font-black flex items-center justify-center gap-2 disabled:opacity-40 shadow-lg">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate Image</>}
          </button>
          {result && (
            <div className="rounded-xl border-2 border-emerald-400/40 bg-slate-950 p-2 space-y-2">
              <img src={result} alt="Generated" className="w-full rounded-lg max-h-[60vh] object-contain" />
              <button onClick={dl} className="w-full px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Download</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
